import type { PricingSectionContent } from "@site-factory/config";
import { cn } from "../utils";
import { SectionTitle, SectionSubtitle } from "../typography/Typography";
import { Card, CardContent, CardFooter } from "../components/Card";
import { Button } from "../components/Button";
import { Badge } from "../components/Badge";
import { SectionShell } from "../components/SectionShell";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/Tabs";

export interface PricingSectionProps extends PricingSectionContent {
  className?: string;
}

export function PricingSection({
  title,
  subtitle,
  plans,
  planGroups,
  variant: _variant = "cards",
  className,
}: PricingSectionProps) {
  const renderPlans = (planList: typeof plans) => (
    <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3 max-w-5xl mx-auto">
      {planList.map((plan, index) => (
            <Card
              key={index}
              className={cn(
            "p-6 flex flex-col shadow-sm-2 hover:shadow-md-2 transition border border-neutral-200/70 dark:border-white/10 bg-[var(--color-surface,#fff)] text-[var(--color-text,#0f172a)]",
            plan.highlighted && "border-2 border-primary shadow-md-2 relative"
              )}
            >
            {plan.highlighted && (
              <Badge className="absolute -top-3 left-1/2 -translate-x-1/2">
                Most Popular
              </Badge>
            )}

          <CardContent className="p-0 flex-1 space-y-3">
            <h3 className="text-xl font-semibold">{plan.name}</h3>
            <p className="text-3xl font-bold mt-2" style={{ color: "var(--color-text, #0f172a)" }}>
              {plan.price}
            </p>
            {plan.description && (
              <p
                className="text-sm"
                style={{ color: "var(--color-text, #0f172a)", opacity: 0.82 }}
              >
                {plan.description}
              </p>
            )}

            <ul className="mt-4 space-y-2">
              {plan.features.map((feature, featureIndex) => (
                <li
                  key={featureIndex}
                  className="flex items-start gap-2"
                  style={{ color: "var(--color-text, #0f172a)", opacity: 0.88 }}
                >
                  <span className="text-primary mt-1">✓</span>
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </CardContent>

          <CardFooter className="p-0 mt-6">
            <Button
              asChild
              variant={plan.highlighted ? "primary" : "outline"}
              className="w-full"
            >
              <a href={plan.ctaHref}>{plan.ctaText}</a>
            </Button>
          </CardFooter>
        </Card>
      ))}
    </div>
  );

  return (
    <SectionShell className={className} backgroundClassName="bg-surface-alt">
      {(title || subtitle) && (
        <div className="text-center mb-12">
          {title && <SectionTitle>{title}</SectionTitle>}
          {subtitle && <SectionSubtitle className="mt-4">{subtitle}</SectionSubtitle>}
        </div>
      )}

      {planGroups && planGroups.length > 0 ? (
        <Tabs defaultValue={planGroups[0]?.id}>
          <div className="flex flex-col items-center mb-6">
            <TabsList>
              {planGroups.map((group) => (
                <TabsTrigger key={group.id} value={group.id}>
                  {group.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>
          {planGroups.map((group) => (
            <TabsContent
              key={group.id}
              value={group.id}
              className="bg-transparent border-none p-0 shadow-none"
            >
              {renderPlans(group.plans)}
            </TabsContent>
          ))}
        </Tabs>
      ) : (
        renderPlans(plans)
      )}
    </SectionShell>
  );
}
